import React from "react";
import { Box } from "@mui/material";
import { keyframes } from "@mui/system";
import halo from "../../../assets/Images/TempleFailure/halo.svg";
import sunOutline from "../../../assets/Images/TempleFailure/sun-outline.png";
import birds from "../../../assets/Images/TempleFailure/birds.svg";
import tier1 from "../../../assets/Images/TempleFailure/tier-1.svg";
import tier2 from "../../../assets/Images/TempleFailure/tier-2.svg";
import tier3 from "../../../assets/Images/TempleFailure/tier-3.svg";
import tier4 from "../../../assets/Images/TempleFailure/tier-4.svg";
import tier5 from "../../../assets/Images/TempleFailure/tier-5.svg";
import ghostCrown from "../../../assets/Images/TempleFailure/ghost-crown.svg";
import skylineBase from "../../../assets/Images/TempleFailure/skyline-base.svg";
import unlitFlame from "../../../assets/Images/TempleFailure/unlit-flame.svg";
import kalashPiece from "../../../assets/Images/TempleFailure/kalash-piece.svg";

// Figma "Payment failed — Hovering kalash" (3728:7256, step "1 Start"): the village skyline with
// the central temple built up to its fifth tier, its crown only a faint outline and the lamp at
// its foot unlit. The kalash comes down on its rope and hovers, swaying, where the crown should
// be — the offering that didn't land. Played once on load, then the sway loops.
// Positions are % of the 1344×392 frame so the scene scales; with "reduce motion" it shows at rest.

const W = 1344;
const H = 392;
const x = (v) => `${(v / W) * 100}%`;
const y = (v) => `${(v / H) * 100}%`;

// Kalash group: rope (84) + kalash (86.8) hung from (672, -196); it ends hovering just above tier 5.
const KALASH_W = 47.6;
const KALASH_H = 84 + 86.8;
const KALASH_DROP = 180; // px on the 392 frame: kalash bottom at y≈155, tier 5 starts at 165.2

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(12%); }
  to   { opacity: 1; transform: translateY(0); }
`;
const descend = keyframes`
  0%   { transform: translateY(0); }
  70%  { transform: translateY(${((KALASH_DROP + 8) / KALASH_H) * 100}%); }
  100% { transform: translateY(${(KALASH_DROP / KALASH_H) * 100}%); }
`;
const sway = keyframes`
  0%, 100% { transform: rotate(-2.5deg); }
  50%      { transform: rotate(2.5deg); }
`;
const ghostIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 0.55; }
`;

const motionSafe = (styles) => ({ "@media (prefers-reduced-motion: no-preference)": styles });

const Layer = ({ src, left, top, width, height, sx }) => (
  <Box
    component="img"
    src={src}
    alt=""
    sx={{ position: "absolute", left: x(left), top: y(top), width: x(width), height: y(height), ...sx }}
  />
);

const TIERS = [
  { src: tier1, left: 571.2, top: 294, width: 201.6, height: 42 },
  { src: tier2, left: 588, top: 257.6, width: 168, height: 39.2 },
  { src: tier3, left: 603.4, top: 224, width: 137.2, height: 36.4 },
  { src: tier4, left: 616, top: 193.2, width: 112, height: 33.6 },
  { src: tier5, left: 627.2, top: 165.2, width: 89.6, height: 30.8 },
];

// width: CSS width of the scene (height follows the 1344×392 ratio).
const HoveringKalash = ({ width = `min(${W}px, 100%)` }) => (
  <Box aria-hidden="true" sx={{ position: "relative", width, aspectRatio: `${W} / ${H}`, mx: "auto", overflow: "hidden" }}>
    <Layer src={halo} left={462} top={-44.8} width={420} height={420} />
    <Layer src={sunOutline} left={574} top={67.2} width={196} height={196} />
    <Layer src={birds} left={0} top={0} width={W} height={168} sx={motionSafe({ animation: `${fadeIn} 1.2s ease-out 0.6s both` })} />

    {/* Central temple, built tier by tier from the ground up */}
    {TIERS.map((t, i) => (
      <Layer key={i} {...t} sx={motionSafe({ animation: `${riseIn} 0.5s cubic-bezier(0.22, 0.61, 0.36, 1) ${0.15 + i * 0.12}s both` })} />
    ))}
    {/* The crown that isn't there yet */}
    <Layer
      src={ghostCrown}
      left={647.4}
      top={38.9}
      width={49.28}
      height={100.52}
      sx={{ opacity: 0.55, ...motionSafe({ animation: `${ghostIn} 0.8s ease-out 1.9s both` }) }}
    />
    <Layer src={skylineBase} left={0} top={0} width={W} height={H} />
    <Layer src={unlitFlame} left={651} top={336} width={42} height={30.8} />

    {/* Kalash on its rope: lowers, then keeps swaying above the empty crown */}
    <Box
      sx={{
        position: "absolute",
        left: x(672 - KALASH_W / 2),
        top: y(-196),
        width: x(KALASH_W),
        height: y(KALASH_H),
        transform: `translateY(${(KALASH_DROP / KALASH_H) * 100}%)`,
        ...motionSafe({ animation: `${descend} 1.6s cubic-bezier(0.33, 0, 0.2, 1) 0.6s both` }),
      }}
    >
      <Box sx={{ position: "absolute", inset: 0, transformOrigin: "50% 0", ...motionSafe({ animation: `${sway} 3.2s ease-in-out 2.2s infinite` }) }}>
        <Box sx={{ position: "absolute", left: "50%", top: 0, width: "1.96px", height: `${(84 / KALASH_H) * 100}%`, transform: "translateX(-50%)", background: "rgba(138, 100, 32, 0.9)" }} />
        <Box component="img" src={kalashPiece} alt="" sx={{ position: "absolute", left: 0, bottom: 0, width: "100%", height: `${(86.8 / KALASH_H) * 100}%` }} />
      </Box>
    </Box>
  </Box>
);

export default HoveringKalash;
